/* eslint-disable react/no-array-index-key */
/* eslint-disable no-plusplus */
/* eslint-disable implicit-arrow-linebreak */
import React, { useEffect, useState, useRef } from 'react';

import ReactModal from 'react-modal';
import closeSvg from 'src/assets/close.svg';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { parseFileDetailsToFile } from 'src/utils/fileConvert';
import {
  ButtonContainer,
  CloseIcon,
  Content,
  FormButton,
  FormTitle,
  Header,
  FormRow,
  ModalContent,
  EyeIcon,
  TrashIcon,
  DocumentRow,
  IconsSlot,
} from './styles';

export const QleDocumentsModal: React.FC<{
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  onClose: (files: FileList | []) => void;
}> = ({ isOpen, onClose }) => {
  const [modalIsOpen, setIsOpen] = useState<boolean>(isOpen);
  const { qleFiles } = useTypedSelector(state => state.benefits.data);
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const validationSchema = yup.object().shape({
    files: yup.mixed().test('type', 'We only support .png .jpg .jpeg .pdf', value => {
      const validDocs = [];
      if (value) {
        for (let index = 0; index < value.length; index++) {
          const file = value[index];
          if (
            file.type.includes('application/pdf') ||
            file.type.includes('image/png') ||
            file.type.includes('img/jpg') ||
            file.type.includes('img/jpeg')
          ) {
            validDocs.push(file);
          }
        }
        return validDocs.length === value.length;
      }
      return true;
    }),
  });

  const formik = useFormik<{ files: FileList | [] }>({
    initialValues: {
      files: [],
    },
    validationSchema,
    onSubmit: values => {
      onClose(values.files);
    },
  });

  const handleModal = () => {
    setIsOpen(prevIsOpen => !prevIsOpen);
    onClose(formik.values.files);
  };

  const openFileInput = () => {
    fileInputRef.current?.click();
  };

  // eslint-disable-next-line no-unused-vars
  const clearInputFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    if (fileInputRef.current && formik.values.files) {
      const updatedFileList = Array.from(formik.values.files).filter((file: any, i: number) => i !== index);
      formik.setFieldValue('files', updatedFileList);

      // Create a new DataTransfer object
      const newDataTransfer = new DataTransfer();

      // Add files to the DataTransfer object
      updatedFileList.forEach((file: any) => {
        newDataTransfer.items.add(file);
      });

      fileInputRef.current.files = newDataTransfer.files;
    }
  };

  const loadedfiles: File[] = [];

  useEffect(() => {
    if (qleFiles) {
      qleFiles.forEach((data: unknown) => {
        const parsedFile = parseFileDetailsToFile(data);
        loadedfiles.push(parsedFile);
      });

      if (loadedfiles) {
        const updatedFileList = Array.from(loadedfiles);

        // Create a new DataTransfer object
        const newDataTransfer = new DataTransfer();

        // Add files to the DataTransfer object
        updatedFileList.forEach((file: File) => {
          newDataTransfer.items.add(file);
        });

        formik.setFieldValue('files', loadedfiles);
      }
    }
  }, []);

  const openFilePreview = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ReactModal
      isOpen={modalIsOpen}
      ariaHideApp={false}
      onRequestClose={handleModal}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '696px',
          height: '400px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 40px 0 40px',
          overflowY: 'hidden',
        },
      }}
    >
      <Header>
        <FormTitle>{t('benefits.qualifyingLifeEvent.form.documents')}</FormTitle>
        <CloseIcon src={closeSvg} onClick={() => handleModal()} />
      </Header>

      <Content onSubmit={formik.handleSubmit}>
        <ModalContent>
          <input
            ref={fileInputRef}
            style={{ width: '0' }}
            data-testid="qle-document-input"
            data-cy="qle-document-input"
            type="file"
            id="files"
            name="files"
            accept=".png, .jpg, .jpeg, .pdf"
            multiple
            placeholder="Drag and drop or click to upload a file"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { files } = e.target;

              if (files) {
                const joined = Array.from(files).concat(Array.from(formik.values.files));
                formik.setFieldValue('files', joined);
              }
            }}
          />
          <FormRow>
            {Array.from(formik.values.files).map((file: any, index: number) => (
              <DocumentRow stripped={index % 2 > 0} key={index}>
                {file.name}
                <IconsSlot>
                  <EyeIcon onClick={() => openFilePreview(file)} />
                  <TrashIcon
                    onClick={() => {
                      removeFile(index);
                    }}
                  />
                </IconsSlot>
              </DocumentRow>
            ))}
          </FormRow>
        </ModalContent>
        <ButtonContainer>
          <FormButton onClick={openFileInput} type="button">
            {t('benefits.qualifyingLifeEvent.form.attachDocuments')}
          </FormButton>
        </ButtonContainer>
      </Content>
    </ReactModal>
  );
};
